import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Facebook, Bot, MessageCircle, CheckCircle, XCircle,
  RefreshCw, Unlink, ExternalLink, Loader2, Sparkles, TrendingUp, Bell,
  Settings, Send, Clock, Users, UserRound, Mail, Phone, Building2, Zap, MessageSquare
} from 'lucide-react';
import { API_URL } from '../../config';
import { SalesPanel, ConversationsPanel, TelegramPanel } from './PagePanels';
import BotConfigPanel from '../shared/BotConfigPanel';
import SelfConfigRequest from '../shared/SelfConfigRequest';
import CommunityManagerPanel from '../shared/CommunityManagerPanel';
import AgentOffer, { useAgentOffer } from '../shared/AgentOffer';
import TeamConfiguring from '../shared/TeamConfiguring';
import FacebookPlans, { CmSetupCard, CmPromoCard } from '../shared/FacebookPlans';

// ============================================================
// MA PAGE FACEBOOK - Connexion simple pour les clients
// Le client clique "Connecter", choisit sa Page, c'est tout.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

// Libelles humains des permissions Facebook demandees - affiches apres connexion pour
// confirmer explicitement au client (et au reviewer App Review Meta, cf. exigence
// "the app confirming which permissions have been granted") ce qui a ete autorise.
const PERMISSION_LABELS = {
  pages_show_list: 'Voir la liste de vos Pages',
  pages_messaging: 'Envoyer et recevoir des messages sur votre Page',
  pages_manage_metadata: 'Gérer les paramètres de votre Page',
  pages_read_engagement: 'Lire les publications et commentaires de votre Page',
  pages_read_user_content: 'Lire le contenu publié par les visiteurs sur votre Page',
  pages_manage_posts: 'Créer, modifier et supprimer des publications sur votre Page',
  pages_manage_engagement: 'Répondre aux commentaires et gérer les interactions de votre Page',
  business_management: 'Gérer les ressources de votre compte professionnel',
};

export default function FacebookPageConnect({ onBack, onSupport }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [choices, setChoices] = useState(null);   // { state, pages: [...] } renvoyé par la popup
  const [message, setMessage] = useState(null);   // { type: 'ok'|'error', text }
  const [justGranted, setJustGranted] = useState(null); // { pageName, permissions: [...] } après connexion
  const [busyId, setBusyId] = useState(null);
  const [panel, setPanel] = useState(null);       // { clientId, tab: 'sales'|'conversations'|'telegram' }
  const [showCmPlans, setShowCmPlans] = useState(false);
  const { me: offer, loading: offerLoading, reload: reloadOffer } = useAgentOffer('facebook');   // essai gratuit / abonnement
  const popupRef = useRef(null);

  const loadPages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/facebook/pages`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setPages(data.pages);
    } catch (e) {
      console.error('Erreur chargement pages:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  // Écoute le retour de la popup Facebook
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data || {};
      if (data.type === 'fb_connect_pages') {
        setConnecting(false);
        setChoices({ state: data.state, pages: data.pages });
      } else if (data.type === 'fb_connect_error') {
        setConnecting(false);
        setMessage({ type: 'error', text: data.message || 'Erreur de connexion Facebook' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const startConnect = async () => {
    setMessage(null);
    setConnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/facebook/oauth/url`, { headers: authHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      const w = 620, h = 720;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      popupRef.current = window.open(
        data.oauth_url, 'teckia_fb_connect',
        `width=${w},height=${h},left=${left},top=${top}`
      );
      // Si la popup est bloquée
      if (!popupRef.current) {
        setConnecting(false);
        setMessage({ type: 'error', text: 'Popup bloquée par le navigateur. Autorisez les popups pour ce site.' });
      }
    } catch (e) {
      setConnecting(false);
      setMessage({ type: 'error', text: e.message || 'Erreur serveur' });
    }
  };

  const confirmPage = async (pageId) => {
    setBusyId(pageId);
    try {
      const res = await fetch(`${API_URL}/api/facebook/connect-page`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ state: choices.state, page_id: pageId })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'ok', text: data.message });
        setJustGranted({ pageName: data.page_name, permissions: data.granted_permissions || [] });
        setChoices(null);
        loadPages();
        reloadOffer();
      } else {
        setMessage({ type: 'error', text: data.message });
        if (data.needs_offer) { setChoices(null); reloadOffer(); }
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally {
      setBusyId(null);
    }
  };

  const toggleBot = async (page) => {
    setBusyId(page.client_id);
    try {
      const res = await fetch(`${API_URL}/api/clients/${page.client_id}/messenger-bot/toggle`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setPages(prev => prev.map(p => p.client_id === page.client_id ? { ...p, bot_active: data.is_active } : p));
        setMessage({ type: 'ok', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally {
      setBusyId(null);
    }
  };

  const disconnectPage = async (page) => {
    if (!window.confirm(`Déconnecter la page « ${page.page_name} » ? L'agent IA ne répondra plus sur cette page.`)) return;
    setBusyId(page.client_id);
    try {
      const res = await fetch(`${API_URL}/api/facebook/pages/${page.client_id}/disconnect`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'ok', text: 'Page déconnectée' });
        loadPages();
        reloadOffer();
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={onBack} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">Page Facebook</h1>
              <p className="text-xs md:text-sm text-blue-200">Connectez votre page et activez votre agent IA</p>
            </div>
          </div>
        </div>

        {/* Message feedback */}
        {message && (
          <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 ${
            message.type === 'ok'
              ? 'bg-green-500/10 border border-green-500/30 text-green-300'
              : 'bg-red-500/10 border border-red-500/30 text-red-300'
          }`}>
            {message.type === 'ok' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <XCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {/* Confirmation explicite des permissions accordées par Facebook lors de cette
            connexion (Graph /me/permissions, pas une supposition côté app) */}
        {justGranted && (
          <div className="mb-6 p-5 rounded-2xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 text-green-300 font-bold">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                Permissions accordées pour « {justGranted.pageName} »
              </div>
              <button onClick={() => setJustGranted(null)} className="text-green-300/60 hover:text-green-300 text-sm">✕</button>
            </div>
            {justGranted.permissions.length === 0 ? (
              <p className="text-green-200/70 text-sm">Aucune permission détectée — vérifiez la connexion Facebook.</p>
            ) : (
              <ul className="space-y-1.5">
                {justGranted.permissions.map(p => (
                  <li key={p} className="flex items-center gap-2 text-sm text-green-100">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span>{PERMISSION_LABELS[p] || p}</span>
                    <code className="text-green-400/70 text-xs bg-black/20 px-1.5 py-0.5 rounded">{p}</code>
                  </li>
                ))}
              </ul>
            )}
            {(() => {
              const MISSING = ['pages_read_engagement', 'pages_read_user_content', 'pages_manage_posts', 'pages_manage_engagement'].filter(p => !justGranted.permissions.includes(p));
              return MISSING.length > 0 && (
                <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-400/40 text-amber-100 text-sm">
                  <b>⚠️ Non accordées par Facebook :</b> publication des posts et réponses publiques aux commentaires.
                  <div className="mt-1 text-amber-200/80 text-xs">Le robot Messenger fonctionne normalement. Pour publier, notre équipe doit d'abord activer ces droits sur votre Page (validation Meta en cours).</div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Offre : il faut un essai gratuit actif ou un abonnement pour connecter une Page */}
        {offerLoading ? (
          <div className="text-center py-10"><Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto" /></div>
        ) : !offer ? (
          <p className="text-center text-sm text-red-300 py-8">Impossible de charger votre offre. Rechargez la page.</p>
        ) : (
          <>
            {offer.slots.length === 0 && !offer.can_connect
              ? <FacebookPlans meBot={offer} reloadBot={reloadOffer} onSupport={onSupport} />
              : <AgentOffer channel="facebook" me={offer} reload={reloadOffer} show="panel" />}
            {offer.cm_setup_pending && <CmSetupCard onSupport={onSupport} />}
            {offer.slots.length > 0 && !offer.has_cm && !offer.cm_setup_pending && (showCmPlans
              ? <FacebookPlans meBot={offer} reloadBot={reloadOffer} onSupport={onSupport} initialPlan="cm" />
              : <CmPromoCard onOpen={() => setShowCmPlans(true)} />)}
            {!offer.can_connect && offer.slots.length > 0 && pages.length > 0 && (
              <p className="text-center text-blue-200/80 text-sm mb-8">Toutes vos Pages incluses dans l'offre sont utilisées. Ajoutez une Page ci-dessus pour en connecter une autre.</p>
            )}
          </>
        )}

        {offer?.setup_pending && <TeamConfiguring variant="banner" channel="facebook" phone={offer.contact?.phone} onSupport={onSupport} />}

        {offer?.can_connect && (
          <>
            {/* Étapes simples */}
            {pages.length === 0 && !loading && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 text-center mb-8">
                <Sparkles className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h2 className="text-lg md:text-xl font-bold text-white mb-3">Comment ça marche ?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left">
                  {[
                    { n: '1', t: 'Connectez-vous', d: 'Cliquez sur le bouton bleu et connectez-vous avec votre compte Facebook.' },
                    { n: '2', t: 'Choisissez votre page', d: 'Sélectionnez la page de votre business dans la liste.' },
                    { n: '3', t: 'Activez votre agent IA', d: 'Il répondra automatiquement à vos clients, 24h/24.' },
                  ].map(step => (
                    <div key={step.n} className="bg-white/5 rounded-2xl p-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center mb-2">{step.n}</div>
                      <div className="text-white font-semibold text-sm mb-1">{step.t}</div>
                      <div className="text-blue-200 text-xs">{step.d}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bouton connecter */}
            <div className="text-center mb-10">
              <button
                onClick={startConnect}
                disabled={connecting}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-base md:text-lg shadow-2xl transition-all hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
              >
                {connecting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Facebook className="w-6 h-6" />}
                {connecting ? 'Connexion en cours...' : (pages.length > 0 ? 'Connecter une autre page' : 'Connecter avec Facebook')}
              </button>
              {pages.length > 0 && offer.free_slots > 0 && (
                <p className="text-emerald-300 text-sm mt-3 font-semibold">Vous avez {offer.free_slots} emplacement{offer.free_slots > 1 ? 's' : ''} disponible{offer.free_slots > 1 ? 's' : ''} dans votre offre.</p>
              )}
              <p className="text-white/40 text-xs mt-3">
                Connexion sécurisée via Facebook. Nous n'accédons jamais à votre mot de passe.
              </p>
            </div>
          </>
        )}

        {/* Choix de la page (après popup) */}
        {choices && (
          <div className="bg-white/5 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-6 mb-10">
            <h3 className="text-white font-bold mb-4">Choisissez la page à connecter :</h3>
            <div className="space-y-3">
              {choices.pages.map(p => (
                <button
                  key={p.id}
                  onClick={() => confirmPage(p.id)}
                  disabled={busyId === p.id}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 hover:bg-blue-500/20 border border-white/10 hover:border-blue-500/40 transition-all text-left disabled:opacity-60"
                >
                  {p.picture
                    ? <img src={p.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                    : <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center"><Facebook className="w-6 h-6 text-white" /></div>}
                  <div className="flex-1">
                    <div className="text-white font-semibold">{p.name}</div>
                    <div className="text-blue-200 text-xs">Cliquez pour connecter cette page</div>
                  </div>
                  {busyId === p.id && <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pages connectées */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" />
          </div>
        ) : pages.length > 0 && (
          <div>
            <h3 className="text-white font-bold text-lg mb-4">
              Vos pages connectées ({pages.length})
            </h3>
            <div className="space-y-4">
              {pages.map(page => (
                <div key={page.client_id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-5 md:p-6">
                  {page.token_valid !== false && page.missing_permissions?.length > 0 && (
                    <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-400/40 text-amber-100 text-sm">
                      ⚠️ <b>Publication et réponses publiques aux commentaires indisponibles</b> pour cette Page : Facebook n'a pas accordé ces droits à TeckiA (validation Meta en cours). Le robot Messenger fonctionne normalement.
                    </div>
                  )}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                        <Facebook className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold">{page.page_name}</span>
                          {page.page_url && (
                            <a href={page.page_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`w-2 h-2 rounded-full ${page.token_valid === false ? 'bg-red-400' : page.bot_active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
                          <span className={`text-xs ${page.token_valid === false ? 'text-red-300' : page.bot_active ? 'text-green-300' : 'text-gray-400'}`}>
                            {page.token_valid === false
                              ? 'Connexion expirée — reconnexion nécessaire'
                              : page.bot_active ? 'Agent IA actif — il répond à vos clients' : 'Agent IA en pause'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Toggle bot */}
                      <button
                        onClick={() => toggleBot(page)}
                        disabled={busyId === page.client_id || !page.has_config || page.token_valid === false || (!page.bot_active && !page.self_config_allowed)}
                        title={(!page.bot_active && !page.self_config_allowed) ? "L'équipe TeckiA configure puis active votre agent. Vous pouvez demander à le gérer vous-même." : (!page.has_config ? "Configuration en attente par notre équipe" : (page.token_valid === false ? 'Reconnectez votre Page avant de réactiver' : ''))}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all disabled:opacity-50 ${
                          page.bot_active
                            ? 'bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30'
                            : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                        }`}
                      >
                        <Bot className="w-4 h-4" />
                        {page.bot_active ? 'Mettre en pause' : (page.self_config_allowed ? 'Activer l\'agent IA' : '🔒 Activation par l\'équipe')}
                      </button>

                      {/* Déconnecter */}
                      <button
                        onClick={() => disconnectPage(page)}
                        disabled={busyId === page.client_id}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-500/10 text-red-300 border border-red-500/20 hover:bg-red-500/20 text-sm transition-all disabled:opacity-50"
                      >
                        <Unlink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {page.token_valid === false && (
                    <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-xs flex items-center gap-2">
                      <XCircle className="w-4 h-4 flex-shrink-0" />
                      La connexion à cette Page a expiré (souvent parce que la personne qui l'a connectée a perdu son accès à la Page sur Facebook). Déconnectez puis reconnectez la Page pour que l'agent IA fonctionne à nouveau.
                    </div>
                  )}

                  {page.token_valid !== false && !page.has_config && (
                    <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-xs flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 flex-shrink-0" />
                      Notre équipe finalise la configuration de votre agent IA. Vous pourrez l'activer très bientôt.
                    </div>
                  )}

                  {/* Onglets de gestion de la page */}
                  <div className="mt-4 flex gap-2 flex-wrap border-t border-white/10 pt-4">
                    {[
                      { tab: 'sales',         icon: TrendingUp,    label: 'Ventes' },
                      { tab: 'conversations', icon: MessageCircle, label: 'Conversations' },
                      { tab: 'telegram',      icon: Bell,          label: 'Alertes Telegram' },
                      ...(page.self_config_allowed ? [{ tab: 'config', icon: Settings, label: 'Configurer mon agent' }] : []),
                      ...(page.self_config_allowed && page.cm_enabled !== false ? [{ tab: 'cm', icon: Sparkles, label: 'TeckiA CM' }] : []),
                    ].map(({ tab, icon: Icon, label }) => {
                      const isOpen = panel?.clientId === page.client_id && panel?.tab === tab;
                      return (
                        <button
                          key={tab}
                          onClick={() => setPanel(isOpen ? null : { clientId: page.client_id, tab })}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                            isOpen
                              ? 'bg-blue-600 text-white'
                              : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                      );
                    })}
                  </div>

                  {!page.self_config_allowed && <SelfConfigRequest clientId={page.client_id} requested={page.self_config_requested} />}

                  {/* Panneau actif */}
                  {panel?.clientId === page.client_id && (
                    <div className="mt-4 bg-black/20 rounded-2xl p-4">
                      {panel.tab === 'sales' && (
                        <SalesPanel
                          clientId={page.client_id}
                          onOpenConversation={() => setPanel({ clientId: page.client_id, tab: 'conversations' })}
                        />
                      )}
                      {panel.tab === 'conversations' && <ConversationsPanel clientId={page.client_id} />}
                      {panel.tab === 'telegram' && <TelegramPanel clientId={page.client_id} />}
                      {panel.tab === 'config' && <BotConfigPanel clientId={page.client_id} hideActivationToggle />}
                      {panel.tab === 'cm' && <CommunityManagerPanel clientId={page.client_id} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Connexions WhatsApp / Messenger via SendPulse — separees de l'integration officielle */}
        <SendPulseConnectionsSection />

      </div>
    </div>
  );
}

// ============================================================
// WhatsApp & Messenger via SendPulse (connexions pre-configurees par
// l'admin, distinctes de l'integration officielle Facebook ci-dessus)
// ============================================================
function SendPulseConnectionsSection() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [panel, setPanel] = useState(null); // { clientId, tab }
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/my-connections`, { headers: authHeaders() });
      const data = await res.json();
      // Cet ecran est dedie a Facebook : les connexions WhatsApp (channel='whatsapp')
      // sont affichees separement sur l'ecran WhatsApp.
      if (data.success) setConnections(data.connections.filter(c => c.channel === 'facebook'));
    } catch (e) {
      console.error('Erreur chargement connexions SendPulse:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBot = async (conn) => {
    setBusyId(conn.client_id);
    try {
      const res = await fetch(`${API_URL}/api/clients/${conn.client_id}/messenger-bot/toggle`, {
        method: 'POST', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setConnections(prev => prev.map(c => c.client_id === conn.client_id ? { ...c, bot_active: data.is_active } : c));
        setMessage({ type: 'ok', text: data.message });
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur serveur' });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (connections.length === 0) return null;

  return (
    <div className="mt-12">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-5 h-5 text-emerald-400" />
        <h3 className="text-white font-bold text-lg">Messenger</h3>
      </div>
      <p className="text-blue-200/70 text-xs mb-4">
        Connexion temporaire gérée par notre équipe, distincte de votre Page Facebook officielle ci-dessus.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded-xl flex items-center gap-2 text-sm ${
          message.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
            : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="space-y-4">
        {connections.map(conn => (
          <div key={conn.client_id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-5 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  conn.channel === 'facebook' ? 'bg-gradient-to-br from-blue-600 to-indigo-600' : 'bg-gradient-to-br from-green-600 to-emerald-600'
                }`}>
                  {conn.channel === 'facebook' ? <MessageSquare className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold">{conn.name}</span>
                    <span className="text-blue-300 text-xs bg-blue-500/10 px-2 py-0.5 rounded-full">
                      {conn.channel === 'facebook' ? 'Messenger' : 'WhatsApp'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`w-2 h-2 rounded-full ${conn.bot_active ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
                    <span className={`text-xs ${conn.bot_active ? 'text-green-300' : 'text-gray-400'}`}>
                      {conn.bot_active ? 'Agent IA actif — il répond à vos clients' : 'Agent IA en pause'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => toggleBot(conn)}
                  disabled={busyId === conn.client_id || !conn.has_config || (!conn.bot_active && !conn.self_config_allowed)}
                  title={(!conn.bot_active && !conn.self_config_allowed) ? "L'équipe TeckiA configure puis active votre agent." : (!conn.has_config ? "Configuration en attente par notre équipe" : '')}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all disabled:opacity-50 ${
                    conn.bot_active
                      ? 'bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30'
                      : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                  }`}
                >
                  <Bot className="w-4 h-4" />
                  {conn.bot_active ? 'Mettre en pause' : (conn.self_config_allowed ? 'Activer l\'agent IA' : '🔒 Activation par l\'équipe')}
                </button>
              </div>
            </div>

            {!conn.has_config && (
              <div className="mt-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-xs flex items-center gap-2">
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                Notre équipe finalise la configuration de votre agent IA. Vous pourrez l'activer très bientôt.
              </div>
            )}

            {conn.self_config_allowed && (
              <div className="mt-4 flex gap-2 flex-wrap border-t border-white/10 pt-4">
                <button
                  onClick={() => setPanel(panel?.clientId === conn.client_id ? null : { clientId: conn.client_id, tab: 'config' })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                    panel?.clientId === conn.client_id
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <Settings className="w-3.5 h-3.5" /> Configurer mon agent
                </button>
              </div>
            )}

            {panel?.clientId === conn.client_id && (
              <div className="mt-4 bg-black/20 rounded-2xl p-4">
                <BotConfigPanel clientId={conn.client_id} hideActivationToggle />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

